import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, FileText, Image as ImageIcon, Trash2 } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { flagFor } from "@/lib/countries";

const EMPTY = {
  project_id: "", report_date: new Date().toISOString().slice(0, 10),
  work_completed: "", issues: "", delays: "", client_notes: "",
  photos: [], confirmed: false,
};

export default function DailyReports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState({ user_id: "all", project_id: "all", start_date: "", end_date: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [selected, setSelected] = useState(null);

  const reload = async () => {
    const params = {};
    Object.entries(filter).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    const [r, p, u] = await Promise.all([
      api.get("/daily-reports", { params }),
      api.get("/projects"),
      user.role === "admin" ? api.get("/users") : Promise.resolve({ data: [] }),
    ]);
    setReports(r.data); setProjects(p.data); setEmployees(u.data);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter.user_id, filter.project_id, filter.start_date, filter.end_date]);

  const submit = async () => {
    if (!form.project_id || !form.work_completed.trim()) return toast.error("Project and work completed are required");
    if (!form.confirmed) return toast.error("Please confirm the report before submitting");
    try {
      await api.post("/daily-reports", form);
      toast.success("Report submitted");
      setForm(EMPTY); setOpen(false); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photos: [...f.photos, data.filename] }));
      toast.success("Photo attached");
    } catch (err) { toast.error("Photo upload failed"); }
  };

  const removeReport = async (id) => {
    if (!window.confirm("Delete this report? This cannot be undone.")) return;
    try {
      await api.delete(`/daily-reports/${id}`);
      toast.success("Report deleted");
      if (selected?.id === id) setSelected(null);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = (fmt) => {
    const t = localStorage.getItem("cv_token");
    const params = new URLSearchParams({ fmt });
    Object.entries(filter).forEach(([k, v]) => { if (v && v !== "all") params.append(k, v); });
    window.open(`${API}/exports/daily-reports?${params.toString()}&_=${Date.now()}` + (t ? `&token=${t}` : ""), "_blank");
  };

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";
  const empById = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);

  return (
    <div className="space-y-6" data-testid="daily-reports-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">DAILY REPORTS</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight mt-1">Field Logs</h1>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(EMPTY); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-report-btn" className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase">
              <Plus className="h-4 w-4 mr-2" /> Submit Report
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-[#222] rounded-none max-w-2xl text-white max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading uppercase">New Daily Report</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-eyebrow">DATE</Label>
                  <div className="mt-2"><DatePicker value={form.report_date} onChange={(v) => setForm({ ...form, report_date: v })} testid="report-date" /></div>
                </div>
                <div>
                  <Label className="label-eyebrow">PROJECT</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger data-testid="report-project" className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                      {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <FieldTA label="Work Completed *" value={form.work_completed} set={(v) => setForm({ ...form, work_completed: v })} testid="report-work" />
              <FieldTA label="Issues / Risks" value={form.issues} set={(v) => setForm({ ...form, issues: v })} />
              <FieldTA label="Delays" value={form.delays} set={(v) => setForm({ ...form, delays: v })} />
              <FieldTA label="Further Project Notes" value={form.client_notes} set={(v) => setForm({ ...form, client_notes: v })} />

              <div>
                <Label className="label-eyebrow">PHOTOS</Label>
                <input type="file" accept="image/*" onChange={onPhoto} className="block mt-2 text-sm font-mono text-[#bbb]" />
                {form.photos.length > 0 && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {form.photos.map((p) => (
                      <span key={p} className="border border-[#222] px-2 py-1 text-xs font-mono">{p.slice(0, 12)}…</span>
                    ))}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-3 border border-[#222] p-3">
                <Checkbox checked={form.confirmed} onCheckedChange={(v) => setForm({ ...form, confirmed: !!v })} data-testid="report-confirm" />
                <span className="text-sm">I confirm this report is accurate and complete.</span>
              </label>
            </div>
            <DialogFooter>
              <Button onClick={submit} data-testid="submit-report-btn" className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase">Submit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-4 flex flex-wrap items-end gap-3">
        {user.role === "admin" && (
          <div className="min-w-[180px]">
            <Label className="label-eyebrow">EMPLOYEE</Label>
            <Select value={filter.user_id} onValueChange={(v) => setFilter({ ...filter, user_id: v })}>
              <SelectTrigger className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                <SelectItem value="all">All</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="min-w-[180px]">
          <Label className="label-eyebrow">PROJECT</Label>
          <Select value={filter.project_id} onValueChange={(v) => setFilter({ ...filter, project_id: v })}>
            <SelectTrigger className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
              <SelectItem value="all">All</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="label-eyebrow">FROM</Label><div className="mt-2"><DatePicker value={filter.start_date} onChange={(v) => setFilter({ ...filter, start_date: v })} /></div></div>
        <div><Label className="label-eyebrow">TO</Label><div className="mt-2"><DatePicker value={filter.end_date} onChange={(v) => setFilter({ ...filter, end_date: v })} /></div></div>
        {user.role === "admin" && (
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" onClick={() => exportCsv("csv")} className="rounded-none border border-[#222] font-mono text-xs">CSV</Button>
            <Button variant="ghost" onClick={() => exportCsv("xlsx")} className="rounded-none border border-[#222] font-mono text-xs">XLSX</Button>
            <Button variant="ghost" onClick={() => exportCsv("pdf")} className="rounded-none border border-[#222] font-mono text-xs">PDF</Button>
          </div>
        )}
      </Card>

      {/* Split view */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c]">
          <div className="p-4 border-b border-[#1c1c1c] label-eyebrow">REPORTS</div>
          <ul className="divide-y divide-[#1c1c1c]" data-testid="reports-list">
            {reports.length === 0 && <li className="p-5 text-sm font-mono text-[#666]">No reports yet</li>}
            {reports.map((r) => (
              <li
                key={r.id}
                onClick={() => setSelected(r)}
                className={`p-4 cursor-pointer hover:bg-[#101010] ${selected?.id === r.id ? "bg-[#101010] border-l-2 border-white" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm flex items-center gap-2"><FileText className="h-4 w-4 text-[#666]" /> {r.report_date}</div>
                  <span className="label-eyebrow">{projectName(r.project_id)}</span>
                </div>
                <div className="text-xs font-mono text-[#888] mt-1 truncate">
                  {flagFor(empById[r.user_id]?.country)} {r.user_name} · {r.work_completed.slice(0, 70)}
                </div>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c]">
          <div className="p-4 border-b border-[#1c1c1c] flex items-center justify-between">
            <span className="label-eyebrow">DETAILS</span>
            {selected && (user.role === "admin" || selected.user_id === user.id) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removeReport(selected.id)}
                data-testid="delete-report-btn"
                className="rounded-none h-7 px-2 border border-red-500/30 text-red-400 font-heading uppercase text-[10px]"
                title="Delete report"
              >
                <Trash2 className="h-3 w-3 mr-1" /> Delete
              </Button>
            )}
          </div>
          {!selected ? (
            <div className="p-10 text-center text-[#666] font-mono text-sm">SELECT A REPORT TO VIEW</div>
          ) : (
            <div className="p-5 space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <Info k="DATE" v={selected.report_date} />
                <Info k="EMPLOYEE" v={<>{flagFor(empById[selected.user_id]?.country)} {selected.user_name}</>} />
                <Info k="PROJECT" v={projectName(selected.project_id)} />
              </div>
              <Section title="WORK COMPLETED">{selected.work_completed}</Section>
              {selected.issues && <Section title="ISSUES / RISKS">{selected.issues}</Section>}
              {selected.delays && <Section title="DELAYS">{selected.delays}</Section>}
              {selected.client_notes && <Section title="FURTHER PROJECT NOTES">{selected.client_notes}</Section>}
              {selected.photos?.length > 0 && (
                <div>
                  <div className="label-eyebrow mb-2">PHOTOS</div>
                  <div className="grid grid-cols-3 gap-2">
                    {selected.photos.map((p) => (
                      <a key={p} href={`${API}/photos/${p}`} target="_blank" rel="noreferrer" className="border border-[#222] p-2 flex items-center gap-2 text-xs font-mono">
                        <ImageIcon className="h-4 w-4" /> {p.slice(0, 8)}…
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function FieldTA({ label, value, set, testid }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Textarea value={value} onChange={(e) => set(e.target.value)} data-testid={testid} className="bg-black border-[#222] rounded-none mt-2 font-mono text-sm" rows={3} />
    </div>
  );
}
function Info({ k, v }) { return <div><div className="label-eyebrow">{k}</div><div className="mt-1">{v}</div></div>; }
function Section({ title, children }) { return <div><div className="label-eyebrow">{title}</div><div className="mt-2 whitespace-pre-wrap text-[#ddd]">{children}</div></div>; }
